import React, { useState, useEffect } from 'react';
import { X, Send, AlertCircle, Check } from 'lucide-react';
import apiService from './apiService';

const TriageRoutingModal = ({ patient, onComplete, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [nextLocationType, setNextLocationType] = useState('consultation');
    const [availableRooms, setAvailableRooms] = useState([]);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [triageNotes, setTriageNotes] = useState('');
    const [priority, setPriority] = useState('routine');

    useEffect(() => {
        if (nextLocationType) {
            fetchRooms(nextLocationType);
        }
    }, [nextLocationType]);

    const fetchRooms = async (type) => {
        try {
            const rooms = await apiService.getAvailableRooms(type);
            setAvailableRooms(rooms);
            if (rooms.length > 0) {
                setSelectedRoomId(rooms[0].id);
            }
        } catch (error) {
            console.error('Error fetching rooms:', error);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const result = await apiService.completeTriageAndRoute({
                patientId: patient.id,
                triageNotes,
                nextLocationType,
                nextLocationId: selectedRoomId,
                priority
            });
            
            onComplete(result);
        } catch (error) {
            console.error('Error routing patient:', error);
            alert(`Failed to route patient: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const selectedRoom = availableRooms.find(r => r.id === selectedRoomId);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Complete Triage & Route Patient</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {/* Patient Info */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm text-gray-600">Patient:</p>
                        <p className="font-semibold text-gray-900">{patient.name}</p>
                        <p className="text-sm text-gray-600">Queue #{String(patient.queueNumber).padStart(3, '0')}</p>
                    </div>

                    {/* Send Patient To */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Send Patient To <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={nextLocationType}
                            onChange={(e) => setNextLocationType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="consultation">👨‍⚕️ Doctor Consultation</option>
                            <option value="lab">🔬 Laboratory</option>
                            <option value="imaging">📷 Imaging (X-ray/CT)</option>
                            <option value="pharmacy">💊 Pharmacy (Minor case)</option>
                            <option value="emergency">🚨 Emergency Room</option>
                        </select>
                    </div>

                    {/* Room Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Specific Room <span className="text-red-500">*</span>
                        </label>
                        {availableRooms.length > 0 ? (
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {availableRooms.map((room) => (
                                    <div
                                        key={room.id}
                                        onClick={() => setSelectedRoomId(room.id)}
                                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                            selectedRoomId === room.id
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-blue-300'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-gray-900">{room.name}</p>
                                                {room.fullAddress && (
                                                    <p className="text-xs text-gray-500">{room.fullAddress}</p>
                                                )}
                                            </div>
                                            {selectedRoomId === room.id && (
                                                <Check className="w-5 h-5 text-blue-600" />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed">
                                <p className="text-sm text-gray-500">No rooms available</p>
                                <p className="text-xs text-gray-400 mt-1">Contact admin to create rooms</p>
                            </div>
                        )}
                    </div>

                    {/* Priority (for lab/imaging returns) */}
                    {['lab', 'imaging'].includes(nextLocationType) && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => setPriority('routine')}
                                    className={`p-2 rounded-lg border text-sm font-medium ${
                                        priority === 'routine'
                                            ? 'border-green-500 bg-green-50 text-green-800'
                                            : 'border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    Routine
                                </button>
                                <button
                                    onClick={() => setPriority('urgent')}
                                    className={`p-2 rounded-lg border text-sm font-medium ${
                                        priority === 'urgent'
                                            ? 'border-red-500 bg-red-50 text-red-800'
                                            : 'border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    Urgent
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Triage Notes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Triage Notes (Optional)
                        </label>
                        <textarea
                            value={triageNotes}
                            onChange={(e) => setTriageNotes(e.target.value)}
                            rows={3}
                            placeholder="Any important notes for the next provider..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Summary */}
                    {selectedRoom && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-sm font-medium text-gray-900 mb-1">Patient will be sent to:</p>
                            <p className="text-lg font-bold text-blue-600">{selectedRoom.name}</p>
                            {selectedRoom.fullAddress && (
                                <p className="text-xs text-gray-600 mt-1">{selectedRoom.fullAddress}</p>
                            )}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4 border-t">
                        <button
                            onClick={onCancel}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || !selectedRoomId}
                            className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                        >
                            {loading ? 'Sending...' : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Complete & Send
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TriageRoutingModal;

